import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { useUserStore } from '@/hooks/use-user-store';
import { isWeb } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';

const TAG = '[UserSessionLoader]';
/**
 * Waits for the user store to be hydrated, and also does a session refresh on app start, before rendering it's children.
 * This ensure that we wait for the previous session to be restored from AsyncStorage before rendering our app and attempting API calls.
 */
export const UserSessionLoader = (props: PropsWithChildren) => {
    const [refreshCompleted, setRefreshedCompleted] = useState(false);
    const refreshing = useRef<boolean>(false);
    const hydrated = useUserStore(state => state._hydrated);
    const session = useUserStore(state => state.session);

    const { refresh } = useUserStore(state => state.actions);

    const webLoaded = hydrated;
    const appLoaded = hydrated && refreshCompleted;
    const loaded = isWeb ? webLoaded : appLoaded;

    useEffect(() => {
        const refreshSession = async () => {
            if (!refreshCompleted && !refreshing.current) {
                refreshing.current = true;
                try {
                    logger.info(TAG, 'Refreshing session');
                    await refresh(session);
                } catch (e) {
                    logger.warn(TAG, 'Failed to refresh session', e);
                } finally {
                    setRefreshedCompleted(true);
                }
            }
        };

        if (!hydrated) {
            //skip while hydrating;
            return;
        }

        if (isWeb) {
            // don't do the session refresh on web because it will cause losing the user session on each hot reload
            // we also avoid doing a new login on every new tab opened
            setRefreshedCompleted(true);
            return;
        }

        if (session) {
            //User has a session, try to refresh it
            refreshSession();
        } else {
            //No session, mark as "refreshed" so the boostrap continues
            setRefreshedCompleted(true);
        }
    }, [refreshCompleted, session, refresh, hydrated]);

    if (loaded) {
        return <>{props.children}</>;
    } else {
        return <></>;
    }
};
